/**
 * Shipments Section Component
 * Unified design matching Sales Orders page with table/cards toggle
 */

import { useState, useEffect, lazy, Suspense } from 'react'
import { useSelector, useDispatch } from '@ufc/core'
import { useTranslation, useIsRTL } from '@ufc/core'
import { PaginationControls } from '@ufc/uikit'
import { selectIsAdmin } from '@ufc/domains/admin-context'

import {
  selectFilteredShipments,
  selectShipmentStats,
  selectShipmentsLoading,
  selectShipmentsFilter,
  selectShipmentsPagination,
} from '../../../store/shipments.selectors'
import { shipmentsActions } from '../../../store/shipments.slice'
import { useShipmentsReadable } from '../../../copilot/shipments.readable'
import { useShipmentsActions } from '../../../copilot/shipments.actions'
import { useShipmentsSuggestions } from '../../../copilot/shipments.suggestions'
const ShipmentDetailDrawer = lazy(() => import('../../drawers/shipment-detail-drawer').then(m => ({ default: m.ShipmentDetailDrawer })))
import type { Shipment, ShipmentStatus } from '../../../domain/types'

import {
  ShipmentsHeader,
  ShipmentsStats,
  ShipmentsToolbar,
  ShipmentsTable,
  ShipmentsCardView,
  ShipmentsEmptyState,
} from './components'

export function ShipmentsSection() {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const isRTL = useIsRTL()
  const isArabic = i18n.language === 'ar'

  const shipments = useSelector(selectFilteredShipments)
  const stats = useSelector(selectShipmentStats)
  const loading = useSelector(selectShipmentsLoading)
  const filter = useSelector(selectShipmentsFilter)
  const pagination = useSelector(selectShipmentsPagination)
  const isAdmin = useSelector(selectIsAdmin)

  const [searchValue, setSearchValue] = useState(filter.search || '')
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null)
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table')

  // Fetch data when filter or pagination changes
  useEffect(() => {
    dispatch(shipmentsActions.fetchShipments(filter))
  }, [dispatch, filter.page, filter.pageSize, filter.status])

  useEffect(() => {
    setSearchValue(filter.search || '')
  }, [filter.search])

  // Fetch stats once on mount
  useEffect(() => {
    dispatch(shipmentsActions.fetchShipmentStats())
  }, [dispatch])

  // Reset pagination when leaving the page
  useEffect(() => {
    return () => {
      dispatch(shipmentsActions.setPage(1))
    }
  }, [dispatch])

  useShipmentsReadable()
  useShipmentsActions()
  useShipmentsSuggestions()

  const handleSearch = (value: string) => {
    setSearchValue(value)
    dispatch(shipmentsActions.searchShipments(value))
  }

  const handleStatusFilter = (status: string) => {
    dispatch(shipmentsActions.setPage(1)) // Reset to first page on filter change
    dispatch(shipmentsActions.filterByStatus(status as ShipmentStatus | 'all'))
  }

  const handlePageChange = (page: number) => {
    dispatch(shipmentsActions.setPage(page))
  }

  const handlePageSizeChange = (pageSize: number) => {
    dispatch(shipmentsActions.setPageSize(pageSize))
  }

  const handleRowClick = (shipment: Shipment) => {
    setSelectedRowId(shipment.id)
    setSelectedShipment(shipment)
    setDrawerOpen(true)
  }

  const handleRowDoubleClick = (shipment: Shipment) => {
    if (selectedRowId === shipment.id) {
      setSelectedRowId(null)
      setSelectedShipment(null)
      setDrawerOpen(false)
    }
  }

  const handleExport = () => {
    console.log('Export')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ShipmentsHeader isArabic={isArabic} onExport={handleExport} />

      <ShipmentsStats
        stats={stats}
        filterStatus={filter.status || 'all'}
        onStatusFilter={handleStatusFilter}
        isArabic={isArabic}
      />

      <ShipmentsToolbar
        searchValue={searchValue}
        onSearchChange={handleSearch}
        filterValue={filter.status || 'all'}
        onFilterChange={handleStatusFilter}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        isArabic={isArabic}
      />

      {shipments.length === 0 ? (
        <ShipmentsEmptyState isArabic={isArabic} />
      ) : viewMode === 'table' ? (
        <ShipmentsTable
          shipments={shipments}
          selectedRowId={selectedRowId}
          showTenantColumn={isAdmin}
          onRowClick={handleRowClick}
          onRowDoubleClick={handleRowDoubleClick}
          isArabic={isArabic}
        />
      ) : (
        <ShipmentsCardView
          shipments={shipments}
          showTenantColumn={isAdmin}
          onCardClick={handleRowClick}
          isArabic={isArabic}
        />
      )}

      {/* Pagination */}
      {shipments.length > 0 && (
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.total}
          totalPages={pagination.pageCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizes={[10, 20, 50, 100]}
          isRTL={isRTL}
        />
      )}

      <Suspense fallback={null}>
        <ShipmentDetailDrawer
          shipment={selectedShipment}
          open={drawerOpen}
          onOpenChange={(open) => {
            setDrawerOpen(open)
            if (!open) setSelectedRowId(null)
          }}
        />
      </Suspense>
    </div>
  )
}
